import Configs from "../config";
import { CreateCategoryIRoute, UpdateCategoryIRoute } from "../generated/Api";
import { ICategory } from "../types/CategoryTypes";
import { DefaultResponse } from "../types/UtilTypes";
import { localStorageGetItem } from "../utils/LocalStorage";

//System categories, no one can create a category with this names
export const protectedCategories = [
  "imports",
  "updated",
  "email status",
  "webforms",
  "sociallair forms",
];

export interface IDeleteManyCategoriesResponse {
  deletedCategories: string[];
  cantBeDeleted: string[];
}

const create = async (body: Omit<CreateCategoryIRoute, "userUid">) => {
  const userUid = localStorageGetItem("USER_UID");
  const token = localStorageGetItem("USER_TOKEN");
  const response = await fetch(`${Configs.SERVER_URL}/category`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ ...body, userUid }),
  });

  const category: DefaultResponse<UpdateCategoryIRoute> = await response.json();
  if (category.status === "ok") {
    return category.data as UpdateCategoryIRoute;
  }

  throw new Error(category.code);
};

const update = async (
  body: UpdateCategoryIRoute
): Promise<UpdateCategoryIRoute> => {
  const token = localStorageGetItem("USER_TOKEN");
  const response = await fetch(`${Configs.SERVER_URL}/category`, {
    method: "PATCH",
    headers: {
      Accept: "application/json",
      "Content-type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify(body),
  });

  const tag: DefaultResponse<{ result: UpdateCategoryIRoute }> =
    await response.json();
  if (tag.status === "ok") {
    return tag.data.result as UpdateCategoryIRoute;
  }

  throw new Error(tag.code);
};

const readAll = async () => {
  const userUid = localStorageGetItem("USER_UID");
  const token = localStorageGetItem("USER_TOKEN");
  const response = await fetch(
    `${Configs.SERVER_URL}/category/userUid/${userUid}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-type": "application/json",
        Authorization: "Bearer " + token,
      },
    }
  );

  const tags: DefaultResponse<UpdateCategoryIRoute[]> = await response.json();
  if (tags.status === "ok") {
    return tags.data as UpdateCategoryIRoute[];
  }

  throw new Error(tags.code);
};

const remove = async (categoryId: string): Promise<ICategory> => {
  const token = localStorageGetItem("USER_TOKEN");
  const response = await fetch(`${Configs.SERVER_URL}/category/${categoryId}`, {
    method: "DELETE",
    headers: {
      Authorization: "Bearer " + token,
    },
  });

  const tag: DefaultResponse<ICategory> = await response.json();
  if (tag.status === "ok") {
    return tag.data as ICategory;
  }

  throw new Error(tag.code);
};

const removeMany = async (
  categoryIds: string[]
): Promise<IDeleteManyCategoriesResponse> => {
  const token = localStorageGetItem("USER_TOKEN");
  const response = await fetch(`${Configs.SERVER_URL}/category/delete_many`, {
    method: "DELETE",
    headers: {
      Accept: "application/json",
      "Content-type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ categoryIds }),
  });

  const tag: DefaultResponse<IDeleteManyCategoriesResponse> =
    await response.json();
  if (tag.status === "ok") {
    return tag.data as IDeleteManyCategoriesResponse;
  }

  throw new Error(tag.code);
};

export interface ICheckCategoriesDeleteResponse {
  categoriesReadyToBeDeletedIds: string[];
  categoriesRestricted: string[];
}

const checkCategoriesDelete = async (
  categoryIds: string[]
): Promise<ICheckCategoriesDeleteResponse> => {
  const token = localStorageGetItem("USER_TOKEN");
  const response = await fetch(
    `${Configs.SERVER_URL}/category/delete_many/check`,
    {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        "Content-type": "application/json",
        Authorization: "Bearer " + token,
      },
      body: JSON.stringify({ categoryIds }),
    }
  );

  const categories: DefaultResponse<ICheckCategoriesDeleteResponse> =
    await response.json();
  if (categories.status === "ok") {
    return categories.data as ICheckCategoriesDeleteResponse;
  }

  throw new Error(categories.code);
};

export const CategoriesAPI = {
  create,
  update,
  readAll,
  remove,
  removeMany,
  checkCategoriesDelete,
};
